/**
 * PushToGithubModal — confirmation dialog for pushing the app's latest
 * TilePacket commit to a GitHub branch.
 *
 * Push is treated as a critical action: it writes to an external system
 * the user controls, can't be silently undone, and shows up in the team's
 * git history. So this modal:
 *   - Surfaces the repo + branch + author up front
 *   - Requires a commit message (with a sane auto-default)
 *   - Confirms file count + last commit id
 *   - Shows in-flight / error state inline
 *
 * Used by both:
 *   - Dashboard app card → "Push to <repo>" menu item
 *   - Editor top-bar Push button
 */

import { StyleSheet, css } from 'aphrodite';
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { githubApi } from '@/features/editor/workspaces/versions/api/github.api';

export interface PushResult {
  success: boolean;
  gitSha: string;
  saveId: string;
  branch: string;
  repo: string;
  message: string;
  fileCount: number;
}

interface Props {
  appId: string;
  /** Optional override of the app name shown in the modal title. */
  appName?: string;
  onClose: () => void;
  onSuccess?: (result: PushResult) => void;
  /** Optional pre-fill of the commit message field. */
  defaultMessage?: string;
  /**
   * Editor-level dirty flag: true when the user has unsaved edits in the
   * working copy. Push only ships saved commits, so when dirty=true we
   * block confirmation and prompt the user to save first.
   */
  dirty?: boolean;
  /**
   * Optional one-click "Save & push" callback. Wired by the editor to
   * its `saveToCommitApi`. Resolves when the save commit lands so the
   * modal can immediately proceed with the push. If omitted, the modal
   * just instructs the user to save manually.
   */
  onRequestSave?: () => Promise<void>;
  /** Display name of the open branch when push was triggered from the editor. */
  branchName?: string;
}

interface ConnectionState {
  connected: boolean;
  repo: string;
  defaultBranch: string;
  githubLogin?: string;
}

export default function PushToGithubModal(props: Props) {
  const { appId, appName, onClose, onSuccess, defaultMessage, dirty, onRequestSave, branchName } =
    props;

  const [config, setConfig] = useState<ConnectionState | null>(null);
  const [configError, setConfigError] = useState<string | null>(null);
  const [message, setMessage] = useState<string>(defaultMessage || '');
  const [branch, setBranch] = useState<string>(branchName || '');
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [submittingLabel, setSubmittingLabel] = useState<string>('Pushing…');
  const [error, setError] = useState<string | null>(null);
  /** Drift summary for the branch being pushed — lets users see what's
   *  about to land on GitHub and warns if git has advanced. */
  const [driftSummary, setDriftSummary] = useState<{
    unpushed: number;
    gitAhead: boolean;
    cloudBase: string | null;
    gitHead: string | null;
  } | null>(null);
  const messageRef = useRef<HTMLTextAreaElement>(null);

  // Fetch repo + branch info on open. We also auto-focus the message field
  // once the config arrives so the user can start typing immediately.
  useEffect(() => {
    let cancelled = false;
    githubApi
      .getConfig(appId)
      .then((c) => {
        if (cancelled) return;
        if (!c.connected) {
          setConfigError('This app is not connected to a GitHub repository.');
          return;
        }
        setConfig({
          connected: true,
          repo: c.repo,
          defaultBranch: c.defaultBranch,
          githubLogin: c.githubLogin,
        });
        // Prefer the editor's currently-open branch if provided; otherwise
        // default to the repo's default branch.
        const targetBranch = branchName || c.defaultBranch;
        setBranch((prev) => prev || targetBranch);
        // Defer focus so the modal is fully mounted first.
        setTimeout(() => messageRef.current?.focus(), 30);

        // Drift summary for the target branch — lets the user see what
        // they're about to push and warns if git has advanced since
        // the cloud saves were started.
        if (targetBranch) {
          githubApi
            .getBranchDrift(appId, targetBranch)
            .then((d) => {
              if (cancelled) return;
              if (d.unpushedSaveCount > 0 || d.hasDrift) {
                setDriftSummary({
                  unpushed: d.unpushedSaveCount,
                  gitAhead: d.hasDrift,
                  cloudBase: d.cloudBaseSha,
                  gitHead: d.currentGitSha,
                });
              }
            })
            .catch(() => {
              /* non-blocking */
            });
        }
      })
      .catch((e) => {
        if (!cancelled) setConfigError(e?.message || 'Failed to load GitHub config.');
      });
    return () => {
      cancelled = true;
    };
  }, [appId]);

  // Esc to cancel; ⌘/Ctrl-Enter to submit.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !submitting) onClose();
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
        e.preventDefault();
        void handleConfirm();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitting, message, branch, config]);

  /**
   * Run the actual push. Called once we're sure the working copy is saved
   * (either it already was, or we just saved via onRequestSave).
   */
  const doPush = async () => {
    if (!config) return;
    setSubmittingLabel('Pushing…');
    const res = await githubApi.pushNow(appId, {
      branch: branch.trim() || config.defaultBranch,
      message: message.trim(),
    });
    onSuccess?.(res as PushResult);
    onClose();
  };

  const handleConfirm = async () => {
    if (submitting) return;
    if (!config) return;
    if (!message.trim()) {
      setError('Commit message is required.');
      messageRef.current?.focus();
      return;
    }
    // Block: push must ship saved commits only. If dirty and we have a
    // save callback, run save first; otherwise tell the user to save.
    if (dirty && !onRequestSave) {
      setError('You have unsaved changes. Save the app first, then push.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      if (dirty && onRequestSave) {
        setSubmittingLabel('Saving…');
        await onRequestSave();
      }
      await doPush();
    } catch (e: any) {
      setError(e?.message || 'Push failed.');
      setSubmitting(false);
    }
  };

  /** Save-only path: when the user clicks "Save first" without committing
   *  to a push. Closes the modal afterward — user can re-open to push. */
  const handleSaveOnly = async () => {
    if (!onRequestSave || submitting) return;
    setSubmitting(true);
    setSubmittingLabel('Saving…');
    setError(null);
    try {
      await onRequestSave();
      onClose();
    } catch (e: any) {
      setError(e?.message || 'Save failed.');
      setSubmitting(false);
    }
  };

  const body = (
    <div
      className={css(styles.bg)}
      onClick={() => {
        if (!submitting) onClose();
      }}
    >
      <div
        className={css(styles.box)}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="push-modal-title"
      >
        <div className={css(styles.header)}>
          <span className={css(styles.icon)} aria-hidden>
            ⎇
          </span>
          <span id="push-modal-title" className={css(styles.title)}>
            Push to GitHub
          </span>
          {appName && <span className={css(styles.subtitle)}>· {appName}</span>}
        </div>

        <div className={css(styles.body)}>
          {!config && !configError && (
            <div className={css(styles.loading)}>Loading repository info…</div>
          )}

          {configError && (
            <>
              <div className={css(styles.error)}>{configError}</div>
              <div className={css(styles.actions)}>
                <button type="button" className={css(styles.btn)} onClick={onClose}>
                  Close
                </button>
              </div>
            </>
          )}

          {config && (
            <>
              <div className={css(styles.contextRow)}>
                <Pair label="Repository">
                  <span className={css(styles.mono)}>{config.repo}</span>
                </Pair>
                <Pair label="As">
                  <span className={css(styles.mono)}>{config.githubLogin || '—'}</span>
                </Pair>
              </div>

              <Field label="Branch">
                <input
                  type="text"
                  className={css(styles.input, styles.mono)}
                  value={branch}
                  onChange={(e) => setBranch(e.target.value)}
                  placeholder={config.defaultBranch}
                  disabled={submitting}
                />
                <div className={css(styles.hint)}>
                  The branch must exist on GitHub; this push will create a new commit on its HEAD.
                  Leave as <b className={css(styles.mono)}>{config.defaultBranch}</b> to push to
                  default.
                </div>
              </Field>

              <Field label="Commit message" required>
                <textarea
                  ref={messageRef}
                  className={css(styles.input, styles.textarea)}
                  rows={3}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="What changed in this push?"
                  disabled={submitting}
                />
                <div className={css(styles.hint)}>
                  Shows up in the team's git log. Use the imperative mood (“fix login bug”, not
                  “fixed login bug”).
                </div>
              </Field>

              {dirty ? (
                <div className={css(styles.dirty)}>
                  <span aria-hidden style={{ fontSize: 16 }}>
                    ●
                  </span>
                  <div>
                    <div style={{ fontWeight: 700, marginBottom: 2 }}>Unsaved changes</div>
                    Push only ships <b>saved</b> commits to GitHub. Save the app first
                    {onRequestSave ? (
                      <>
                        {' '}
                        — or click <b>Save &amp; push</b> below to do both in one step.
                      </>
                    ) : (
                      <>, then re-open this dialog to push.</>
                    )}
                  </div>
                </div>
              ) : (
                <div className={css(styles.warning)}>
                  <span aria-hidden>⚠</span>
                  <div>
                    Pushing publishes your latest <b>cloud</b> commit to GitHub. Saves stay in the
                    cloud by default — this is the only way to mirror to GitHub. Anyone with access
                    to <span className={css(styles.mono)}>{config.repo}</span> will see this change.
                    This action <b>cannot be undone</b> — you can only push a new commit on top.
                  </div>
                </div>
              )}

              {driftSummary && (driftSummary.unpushed > 0 || driftSummary.gitAhead) && (
                <div className={css(driftSummary.gitAhead ? styles.dirty : styles.warning)}>
                  <span aria-hidden style={{ fontSize: 16 }}>
                    {driftSummary.gitAhead ? '⚠' : '↑'}
                  </span>
                  <div>
                    <div style={{ fontWeight: 700, marginBottom: 2 }}>
                      {driftSummary.gitAhead
                        ? 'Cloud and git have diverged'
                        : `Pushing ${driftSummary.unpushed} cloud save${
                            driftSummary.unpushed === 1 ? '' : 's'
                          } as one git commit`}
                    </div>
                    {driftSummary.gitAhead ? (
                      <>
                        Your cloud chain is based on{' '}
                        <span className={css(styles.mono)}>
                          {(driftSummary.cloudBase || '').slice(0, 7) || '—'}
                        </span>
                        , but git has moved to{' '}
                        <span className={css(styles.mono)}>
                          {(driftSummary.gitHead || '').slice(0, 7) || '—'}
                        </span>
                        . Pushing will overwrite git's newer commit. Consider cancelling, reopening
                        this branch in the editor to resolve the drift first.
                      </>
                    ) : (
                      <>
                        All {driftSummary.unpushed} saves will be squashed into a single git commit
                        with the message you type below.
                      </>
                    )}
                  </div>
                </div>
              )}

              {error && <div className={css(styles.error)}>{error}</div>}

              <div className={css(styles.actions)}>
                <button
                  type="button"
                  className={css(styles.btn)}
                  onClick={onClose}
                  disabled={submitting}
                >
                  Cancel
                </button>

                {dirty && onRequestSave && (
                  <button
                    type="button"
                    className={css(styles.btn)}
                    onClick={handleSaveOnly}
                    disabled={submitting}
                    title="Save without pushing — re-open this dialog when ready to push"
                  >
                    Save only
                  </button>
                )}

                <button
                  type="button"
                  className={css(styles.btn, styles.btnPrimary)}
                  onClick={handleConfirm}
                  disabled={submitting || !message.trim() || (dirty && !onRequestSave)}
                  title={
                    dirty && !onRequestSave
                      ? 'Save the app first, then push.'
                      : dirty && onRequestSave
                      ? 'Save the current edits, then push them to GitHub'
                      : undefined
                  }
                >
                  {submitting ? (
                    <span className={css(styles.btnInner)}>
                      <span className={css(styles.spinner)} aria-hidden />
                      <span>{submittingLabel}</span>
                    </span>
                  ) : dirty && onRequestSave ? (
                    <span className={css(styles.btnInner)}>
                      <span aria-hidden>⎇</span>
                      <span>Save &amp; push</span>
                    </span>
                  ) : (
                    <span className={css(styles.btnInner)}>
                      <span aria-hidden>⎇</span>
                      <span>Confirm push</span>
                    </span>
                  )}
                </button>
              </div>
              <div className={css(styles.kbdHint)}>
                <kbd>Esc</kbd> to cancel · <kbd>⌘</kbd>+<kbd>↵</kbd> to confirm
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );

  if (typeof document === 'undefined') return null;
  return createPortal(body, document.body);
}

function Pair({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className={css(styles.pair)}>
      <span className={css(styles.pairLabel)}>{label}</span>
      <span className={css(styles.pairValue)}>{children}</span>
    </div>
  );
}

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label className={css(styles.field)}>
      <span className={css(styles.fieldLabel)}>
        {label}
        {required && <span className={css(styles.req)}> *</span>}
      </span>
      {children}
    </label>
  );
}

const spin = {
  '0%': { transform: 'rotate(0deg)' },
  '100%': { transform: 'rotate(360deg)' },
};

const fadeIn = {
  '0%': { opacity: 0, transform: 'translateY(-6px) scale(0.98)' },
  '100%': { opacity: 1, transform: 'translateY(0) scale(1)' },
};

const styles = StyleSheet.create({
  bg: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(15, 23, 42, 0.5)',
    backdropFilter: 'blur(2px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 5000,
  },
  box: {
    width: 'min(520px, 92vw)',
    maxHeight: '88vh',
    overflow: 'auto',
    background: 'var(--bg0, #fff)',
    borderRadius: 12,
    border: '1px solid var(--b1, rgba(0,0,0,0.08))',
    boxShadow: '0 30px 80px rgba(0, 0, 0, 0.28)',
    color: 'var(--t1, #111)',
    fontFamily: 'system-ui, -apple-system, sans-serif',
    animationName: [fadeIn],
    animationDuration: '180ms',
    animationTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)',
    animationFillMode: 'both',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '14px 18px',
    borderBottom: '1px solid var(--b1, rgba(0,0,0,0.08))',
  },
  icon: {
    fontSize: 18,
    color: 'var(--acc, #2563EB)',
    width: 24,
    height: 24,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 6,
    background: 'rgba(37, 99, 235, 0.08)',
  },
  title: { fontSize: 14, fontWeight: 700 },
  subtitle: { fontSize: 12, color: 'var(--t3, #6b7280)' },
  body: { padding: '14px 18px', display: 'flex', flexDirection: 'column', gap: 14 },
  loading: { fontSize: 12, color: 'var(--t3, #6b7280)', padding: 10 },

  contextRow: { display: 'flex', gap: 18, flexWrap: 'wrap' },
  pair: { display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 },
  pairLabel: {
    fontSize: 10,
    fontWeight: 700,
    color: 'var(--t3, #6b7280)',
    letterSpacing: 0.4,
    textTransform: 'uppercase',
  },
  pairValue: { fontSize: 12 },

  field: { display: 'flex', flexDirection: 'column', gap: 6 },
  fieldLabel: { fontSize: 11, fontWeight: 600, color: 'var(--t2, #374151)' },
  req: { color: 'var(--err, #DC2626)' },
  input: {
    border: '1px solid var(--b1, rgba(0,0,0,0.12))',
    background: 'var(--bg1, #f9fafb)',
    color: 'var(--t1, #111)',
    fontSize: 13,
    padding: '8px 10px',
    borderRadius: 6,
    fontFamily: 'inherit',
    transition: 'border-color 120ms ease, box-shadow 120ms ease',
    ':focus': {
      outline: 'none',
      borderColor: 'var(--acc, #2563EB)',
      boxShadow: '0 0 0 3px rgba(37, 99, 235, 0.18)',
    },
    ':disabled': { opacity: 0.6, cursor: 'not-allowed' },
  },
  textarea: { fontFamily: 'inherit', resize: 'vertical', minHeight: 64 },
  hint: { fontSize: 11, color: 'var(--t3, #6b7280)', lineHeight: 1.45 },
  mono: { fontFamily: '"SFMono-Regular", "Menlo", "Consolas", monospace' },

  warning: {
    display: 'flex',
    gap: 10,
    padding: '10px 12px',
    background: 'rgba(245, 158, 11, 0.10)',
    border: '1px solid rgba(245, 158, 11, 0.35)',
    borderRadius: 8,
    fontSize: 12,
    color: '#92400E',
    lineHeight: 1.5,
  },
  dirty: {
    display: 'flex',
    gap: 10,
    padding: '10px 12px',
    background: 'rgba(220, 38, 38, 0.08)',
    border: '1px solid rgba(220, 38, 38, 0.30)',
    borderRadius: 8,
    fontSize: 12,
    color: '#7f1d1d',
    lineHeight: 1.5,
  },
  error: {
    padding: '8px 10px',
    fontSize: 12,
    color: 'var(--err, #DC2626)',
    background: 'rgba(220, 38, 38, 0.08)',
    border: '1px solid rgba(220, 38, 38, 0.25)',
    borderRadius: 6,
  },

  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 8,
    marginTop: 4,
  },
  btn: {
    border: '1px solid var(--b1, rgba(0,0,0,0.12))',
    background: 'var(--bg1, #fff)',
    color: 'var(--t1, #111)',
    fontSize: 12,
    fontWeight: 600,
    padding: '7px 14px',
    borderRadius: 6,
    cursor: 'pointer',
    fontFamily: 'inherit',
    transition: 'background-color 120ms ease, box-shadow 120ms ease, transform 100ms ease',
    ':hover': { background: 'var(--bg2, #f3f4f6)' },
    ':active': { transform: 'translateY(0.5px) scale(0.985)' },
    ':disabled': { opacity: 0.5, cursor: 'not-allowed' },
  },
  btnPrimary: {
    background: 'var(--acc, #2563EB)',
    borderColor: 'var(--acc, #2563EB)',
    color: '#fff',
    ':hover': { background: '#1d4ed8' },
  },
  btnInner: { display: 'inline-flex', alignItems: 'center', gap: 6 },
  spinner: {
    display: 'inline-block',
    width: 10,
    height: 10,
    borderRadius: '50%',
    border: '2px solid rgba(255,255,255,0.35)',
    borderTopColor: '#fff',
    animationName: [spin],
    animationDuration: '0.7s',
    animationIterationCount: 'infinite',
    animationTimingFunction: 'linear',
  },
  kbdHint: {
    textAlign: 'right',
    fontSize: 10,
    color: 'var(--t3, #6b7280)',
    marginTop: -4,
  },
});
